import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { PlatformConfig, Message, AgentProfile, Conversation, ConversationMessage } from '../types';
import { ChatService } from '../services/geminiService';

interface ChatWidgetProps {
  config: PlatformConfig;
  onEscalate: (text: string, email?: string) => void;
}

type ChatPhase = 'pre-chat' | 'queuing' | 'connected' | 'ended';

const BACKEND_URL = (import.meta as any).env.VITE_API_URL || 'http://localhost:3001';

const ChatWidget: React.FC<ChatWidgetProps> = ({ config, onEscalate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [phase, setPhase] = useState<ChatPhase>('pre-chat');
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [inputValue, setInputValue] = useState('');

  // Pre-chat form
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [formError, setFormError] = useState('');

  // Conversation state
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [activeAgent, setActiveAgent] = useState<AgentProfile | null>(null);
  const [queuePosition, setQueuePosition] = useState(0);

  // UI state
  const [isAgentTyping, setIsAgentTyping] = useState(false);
  const [showEndChatConfirm, setShowEndChatConfirm] = useState(false);

  // Inactivity timer
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<Date>(new Date());
  const inactivityPromptSentRef = useRef<boolean>(false);

  const chatServiceRef = useRef<ChatService | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isAgentTyping]);

  // Inactivity detection (5 minutes) - start timer when connected, reset on user activity
  useEffect(() => {
    if (phase === 'connected' && conversation) {
      // Check if we should start/reset the timer
      const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;

      // Only manage timer when: no messages yet OR user just sent a message
      if (!lastMessage || lastMessage.role === 'user') {
        // Clear existing timer
        if (inactivityTimerRef.current) {
          clearTimeout(inactivityTimerRef.current);
        }

        // Reset inactivity prompt flag on user messages
        if (lastMessage?.role === 'user') {
          inactivityPromptSentRef.current = false;
        }

        lastActivityRef.current = new Date();

        // Set 5-minute inactivity timer
        inactivityTimerRef.current = setTimeout(() => {
          handleInactivityPrompt();
        }, 5 * 60 * 1000); // 5 minutes
      }
      // Don't clear timer when agent sends a message - let it keep running
    }

    // Cleanup only when component unmounts or phase changes
    return () => {
      if (phase !== 'connected' && inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [phase, conversation, messages]);

  /**
   * Handle pre-chat form submission
   */
  const handlePreChatSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    // Validation
    if (!userName.trim()) {
      setFormError('Please enter your name');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!userEmail.trim() || !emailRegex.test(userEmail)) {
      setFormError('Please enter a valid email address');
      return;
    }

    // Start conversation
    setPhase('queuing');

    try {
      console.log('[ChatWidget] Starting conversation with:', {
        siteId: config.id,
        userName: userName.trim(),
        userEmail: userEmail.trim()
      });

      const response = await fetch(`${BACKEND_URL}/api/conversations/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId: config.id,
          userName: userName.trim(),
          userEmail: userEmail.trim()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('[ChatWidget] Failed to start conversation:', {
          status: response.status,
          error: data.error,
          message: data.message,
          details: data.details
        });

        // Show detailed error message to user
        const errorMsg = data.message || data.error || 'Failed to start conversation';
        setFormError(errorMsg);
        throw new Error(errorMsg);
      }

      console.log('[ChatWidget] Conversation started successfully:', data.conversation.id);

      setConversation(data.conversation);
      setActiveAgent(data.agent);
      setQueuePosition(data.queuePosition || 0);

      // Initialize chat service
      chatServiceRef.current = new ChatService(config, data.agent);

      // If agent available, connect immediately
      if (data.available) {
        setPhase('connected');
        setMessages(data.conversation.messages || []);
      } else {
        // Show queue position
        setPhase('queuing');
        // In a real app, you'd poll for queue updates
        setTimeout(() => {
          setPhase('connected');
          setMessages(data.conversation.messages || []);
        }, Math.min(data.queuePosition * 2000, 10000));
      }

    } catch (error: any) {
      console.error('[ChatWidget] Error starting conversation:', {
        message: error.message,
        stack: error.stack
      });
      setFormError(error.message || 'Failed to connect. Please try again.');
      setPhase('pre-chat');
    }
  };

  /**
   * Handle sending a message
   */
  const handleSendMessage = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!inputValue.trim() || !conversation || !chatServiceRef.current) return;

    const userMessage: ConversationMessage = {
      id: Date.now().toString(),
      role: 'user',
      text: inputValue.trim(),
      timestamp: new Date()
    };

    // Add to UI immediately
    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');

    // Save message to backend
    try {
      await fetch(`${BACKEND_URL}/api/conversations/${conversation.id}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage.text,
          role: 'user'
        })
      });
    } catch (error) {
      console.error('Failed to save message:', error);
    }

    // Check for email escalation
    const emailRegex = /[\w.-]+@[\w.-]+\.\w+/;
    if (emailRegex.test(userMessage.text)) {
      const email = userMessage.text.match(emailRegex)?.[0];
      onEscalate(userMessage.text, email);
    }

    // Natural delay before showing typing indicator (1-3 seconds)
    const thinkingDelay = 1000 + Math.random() * 2000; // 1-3 seconds

    setTimeout(async () => {
      // Show typing indicator AFTER thinking delay
      setIsAgentTyping(true);

      try {
        if (!chatServiceRef.current) return;

        const stream = await chatServiceRef.current.sendMessageStream(userMessage.text);

        const agentMsgId = (Date.now() + 1).toString();
        let fullText = '';

        // Initialize streaming message
        const streamingMsg: ConversationMessage = {
          id: agentMsgId,
          role: 'agent',
          text: '',
          timestamp: new Date(),
          agentName: activeAgent?.name
        };

        setMessages((prev) => [...prev, streamingMsg]);
        setIsAgentTyping(false);

        // Stream the response
        for await (const chunk of stream) {
          fullText += chunk;
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === agentMsgId ? { ...msg, text: fullText } : msg
            )
          );
        }

        // Save agent message to backend
        await fetch(`${BACKEND_URL}/api/conversations/${conversation.id}/message`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: fullText,
            role: 'agent'
          })
        });

        // Check for escalation keywords
        if (fullText.toLowerCase().includes('escalate') ||
            fullText.toLowerCase().includes('supervisor') ||
            fullText.toLowerCase().includes('specialist')) {
          onEscalate(userMessage.text);

          // Auto-escalate conversation after 3 seconds
          setTimeout(() => {
            handleEscalate('AI suggested escalation to specialist');
          }, 3000);
        }

      } catch (error) {
        console.error('Failed to get AI response:', error);
        setIsAgentTyping(false);

        // Add error message
        setMessages((prev) => [...prev, {
          id: Date.now().toString(),
          role: 'system',
          text: 'Sorry, I\'m having trouble connecting. Please try again.',
          timestamp: new Date()
        }]);
      }
    }, thinkingDelay);
  };

  /**
   * Handle inactivity prompt (5 min idle)
   */
  const handleInactivityPrompt = async () => {
    if (!conversation || phase !== 'connected' || inactivityPromptSentRef.current) return;

    // Mark that we've sent the prompt
    inactivityPromptSentRef.current = true;

    const promptMessage: ConversationMessage = {
      id: Date.now().toString(),
      role: 'agent',
      text: 'Are you still there? Is there anything else I can help you with?',
      timestamp: new Date(),
      agentName: activeAgent?.name
    };

    setMessages((prev) => [...prev, promptMessage]);

    // Save prompt to backend
    await fetch(`${BACKEND_URL}/api/conversations/${conversation.id}/message`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: promptMessage.text,
        role: 'agent'
      })
    }).catch(err => console.error('Failed to save inactivity prompt:', err));

    // If still no response after another 2 minutes, auto-close
    setTimeout(() => {
      const timeSinceLastActivity = Date.now() - lastActivityRef.current.getTime();
      if (timeSinceLastActivity >= 7 * 60 * 1000 && phase === 'connected') { // 7 min total
        // Send final message before ending
        const endMessage: ConversationMessage = {
          id: Date.now().toString(),
          role: 'agent',
          text: "Due to inactivity, I'll be ending this chat session. Thank you for contacting us! Feel free to start a new conversation anytime you need help.",
          timestamp: new Date(),
          agentName: activeAgent?.name
        };
        setMessages((prev) => [...prev, endMessage]);

        // End chat after 3 seconds
        setTimeout(() => {
          handleEndChat(true); // Auto-end
        }, 3000);
      }
    }, 2 * 60 * 1000); // 2 more minutes
  };

  /**
   * Handle ending the chat
   */
  const handleEndChat = async (autoClose: boolean = false) => {
    if (!conversation) return;

    try {
      await fetch(`${BACKEND_URL}/api/conversations/${conversation.id}/end`, {
        method: 'POST'
      });

      // Add system message
      const endMessage: ConversationMessage = {
        id: Date.now().toString(),
        role: 'system',
        text: autoClose
          ? 'Chat ended due to inactivity. Thank you for contacting us!'
          : `Thank you for chatting with us, ${userName}! Have a great day!`,
        timestamp: new Date()
      };

      setMessages((prev) => [...prev, endMessage]);
      setPhase('ended');
      setShowEndChatConfirm(false);

    } catch (error) {
      console.error('Failed to end conversation:', error);
    }
  };

  /**
   * Handle escalation
   */
  const handleEscalate = async (reason: string) => {
    if (!conversation) return;

    try {
      await fetch(`${BACKEND_URL}/api/conversations/${conversation.id}/escalate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });

      const escalationMsg: ConversationMessage = {
        id: Date.now().toString(),
        role: 'agent',
        text: `I've escalated your query to our specialist team. They'll reach out to you at ${userEmail} within 24 hours. Is there anything else I can help with in the meantime?`,
        timestamp: new Date(),
        agentName: activeAgent?.name
      };

      setMessages((prev) => [...prev, escalationMsg]);

    } catch (error) {
      console.error('Failed to escalate conversation:', error);
    }
  };

  /**
   * Reset and start new chat
   */
  const handleStartNewChat = () => {
    setPhase('pre-chat');
    setMessages([]);
    setConversation(null);
    setActiveAgent(null);
    setUserName('');
    setUserEmail('');
    setInputValue('');
    setShowEndChatConfirm(false);
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 font-sans">
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="w-16 h-16 rounded-full shadow-xl flex items-center justify-center text-white transition-transform hover:scale-110"
          style={{ backgroundColor: config.themeColor }}
        >
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="w-96 h-[600px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200">
          {/* Header */}
          <div
            className="px-6 py-4 text-white flex items-center justify-between"
            style={{ backgroundColor: config.themeColor }}
          >
            <div className="flex items-center space-x-3">
              {activeAgent && (
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center font-bold text-lg">
                  {activeAgent.avatarInitials}
                </div>
              )}
              <div>
                <h3 className="font-semibold text-lg">
                  {activeAgent ? activeAgent.name : config.companyName}
                </h3>
                <p className="text-xs opacity-90">
                  {phase === 'queuing' ? 'Connecting...' :
                   phase === 'connected' ? 'Online' :
                   phase === 'ended' ? 'Chat Ended' : 'Live Support'}
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Pre-Chat Form */}
          {phase === 'pre-chat' && (
            <div className="flex-1 flex items-center justify-center p-8">
              <form onSubmit={handlePreChatSubmit} className="w-full space-y-4">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Start a Conversation</h3>
                  <p className="text-sm text-gray-600">We're here to help! Please enter your details.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Your Name</label>
                  <input
                    type="text"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0 focus:border-transparent"
                    style={{ focusRing: config.themeColor } as any}
                    placeholder="John Doe"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Your Email</label>
                  <input
                    type="email"
                    value={userEmail}
                    onChange={(e) => setUserEmail(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0 focus:border-transparent"
                    placeholder="john@example.com"
                    required
                  />
                </div>

                {formError && (
                  <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                    {formError}
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full py-3 rounded-lg text-white font-semibold transition-colors hover:opacity-90"
                  style={{ backgroundColor: config.themeColor }}
                >
                  Start Chat
                </button>

                <p className="text-xs text-gray-500 text-center">
                  By starting a chat, you agree to our privacy policy.
                </p>
              </form>
            </div>
          )}

          {/* Queue Screen */}
          {phase === 'queuing' && (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4"
                     style={{ borderColor: config.themeColor, borderTopColor: 'transparent' }}>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {queuePosition > 0 ? `You're #${queuePosition} in queue` : 'Connecting you...'}
                </h3>
                <p className="text-sm text-gray-600">
                  {queuePosition > 0
                    ? 'We\'ll connect you with the next available agent'
                    : 'Please wait a moment...'}
                </p>
              </div>
            </div>
          )}

          {/* Chat Messages */}
          {(phase === 'connected' || phase === 'ended') && (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                        msg.role === 'user'
                          ? 'text-white'
                          : msg.role === 'system'
                          ? 'bg-gray-200 text-gray-700 text-sm italic'
                          : 'bg-white text-gray-900 border border-gray-200'
                      }`}
                      style={msg.role === 'user' ? { backgroundColor: config.themeColor } : {}}
                    >
                      {msg.role === 'agent' && msg.agentName && (
                        <div className="text-xs font-semibold text-gray-500 mb-1">
                          {msg.agentName}
                        </div>
                      )}
                      <div className="text-sm prose prose-sm max-w-none prose-p:my-2 prose-headings:my-2 prose-ul:my-1 prose-ol:my-1 prose-li:my-0">
                        <ReactMarkdown
                          components={{
                            a: ({node, ...props}) => <a {...props} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800" />,
                            p: ({node, ...props}) => <p {...props} className="mb-2" />,
                            strong: ({node, ...props}) => <strong {...props} className="font-semibold" />,
                            ol: ({node, ...props}) => <ol {...props} className="list-decimal list-inside ml-2 space-y-1" />,
                            ul: ({node, ...props}) => <ul {...props} className="list-disc list-inside ml-2 space-y-1" />,
                            li: ({node, ...props}) => <li {...props} className="ml-2" />
                          }}
                        >
                          {msg.text}
                        </ReactMarkdown>
                      </div>
                      <div className="text-xs opacity-60 mt-1">
                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                ))}

                {isAgentTyping && (
                  <div className="flex justify-start">
                    <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3">
                      <div className="flex space-x-2">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              {phase === 'connected' && (
                <div className="p-4 bg-white border-t border-gray-200">
                  {/* End Chat Confirmation */}
                  {showEndChatConfirm && (
                    <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-900 mb-2">End this conversation?</p>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEndChat(false)}
                          className="px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700"
                        >
                          Yes, End Chat
                        </button>
                        <button
                          onClick={() => setShowEndChatConfirm(false)}
                          className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleSendMessage} className="flex space-x-2">
                    <input
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      placeholder="Type your message..."
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-0"
                      style={{ focusRing: config.themeColor } as any}
                      disabled={isAgentTyping}
                    />
                    <button
                      type="submit"
                      disabled={!inputValue.trim() || isAgentTyping}
                      className="w-10 h-10 rounded-full text-white flex items-center justify-center disabled:opacity-50 transition-colors hover:opacity-90"
                      style={{ backgroundColor: config.themeColor }}
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                    </button>
                  </form>

                  <div className="mt-2 flex justify-between items-center">
                    <p className="text-xs text-gray-500">Powered by {config.companyName}</p>
                    <button
                      onClick={() => setShowEndChatConfirm(true)}
                      className="text-xs text-gray-500 hover:text-gray-700 underline"
                    >
                      End Chat
                    </button>
                  </div>
                </div>
              )}

              {/* Chat Ended State */}
              {phase === 'ended' && (
                <div className="p-4 bg-white border-t border-gray-200 text-center">
                  <button
                    onClick={handleStartNewChat}
                    className="w-full py-3 rounded-lg text-white font-semibold transition-colors hover:opacity-90"
                    style={{ backgroundColor: config.themeColor }}
                  >
                    Start New Conversation
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatWidget;
